import { Tooltip } from '@heroui/react'
import { Crown, Mountain, Award } from 'lucide-react'
import { LocalizedLink } from '~/components/common/localized-link'

export interface AwardItem {
  name: string
  snapshotId?: string
  url?: string
}

interface RankingBadgeProps {
  award: string | AwardItem
  size?: 'sm' | 'md' | 'lg'
}

function parseAwardDetails(name?: string | null) {
  if (!name) return null
  const lower = name.toLowerCase()
  
  let level: 'gold' | 'silver' | 'bronze' | 'hyakumeiten' | 'other' = 'other'
  let shortName = 'Award'
  
  if (lower.includes('gold') || lower.includes('金')) { level = 'gold'; shortName = 'Gold' }
  else if (lower.includes('silver') || lower.includes('銀')) { level = 'silver'; shortName = 'Silver' }
  else if (lower.includes('bronze') || lower.includes('铜') || lower.includes('銅')) { level = 'bronze'; shortName = 'Bronze' }
  else if (lower.includes('百名店') || lower.includes('100')) { level = 'hyakumeiten'; shortName = '百名店' }

  // Extract year (4 digits)
  const yearMatch = name.match(/\b(20\d{2})\b/)
  const year = yearMatch ? yearMatch[1] : ''

  return { level, shortName, year, originalName: name }
}

const BADGE_STYLES = {
  gold: 'bg-white text-yellow-600 border border-yellow-300 shadow-sm',
  silver: 'bg-white text-slate-500 border border-slate-300 shadow-sm',
  bronze: 'bg-white text-amber-700 border border-amber-400 shadow-sm',
  hyakumeiten: 'bg-white text-red-600 border border-red-200 shadow-sm',
  other: 'bg-white text-blue-600 border border-blue-300 shadow-sm',
} as const

export function RankingBadge({ award, size = 'sm' }: RankingBadgeProps) {
  const isObject = typeof award === 'object' && award !== null
  const awardObject = isObject ? (award as AwardItem) : null
  const awardName = awardObject ? awardObject.name : award as string
  const awardUrl = awardObject?.url
  const explicitSnapshotId = awardObject?.snapshotId
  
  const details = parseAwardDetails(awardName)
  if (!details) return null

  const sizeStyles = {
    sm: { container: 'w-10 h-10', icon: 14, text: 'text-[9px]', year: 'text-[8px]', padding: 'p-1' },
    md: { container: 'w-12 h-12', icon: 16, text: 'text-[10px]', year: 'text-[9px]', padding: 'p-1' },
    lg: { container: 'w-14 h-14', icon: 18, text: 'text-xs', year: 'text-[10px]', padding: 'p-1.5' }
  }
  
  const s = sizeStyles[size]
  const Icon = details.level === 'hyakumeiten' ? Mountain : (details.level === 'other' ? Award : Crown)

  const BadgeContent = (
    <div className={`flex flex-col items-center justify-center rounded-full shadow-md transition-transform hover:scale-105 hover:shadow-lg flex-shrink-0 ${s.container} ${s.padding} ${BADGE_STYLES[details.level]}`}>
      <Icon size={s.icon} className={details.year ? 'mb-[1px]' : 'mb-[2px]'} strokeWidth={2.5} />
      <span className={`font-bold leading-none tracking-tight ${s.text}`}>{details.shortName}</span>
      {details.year && <span className={`font-bold mt-[2px] leading-none ${s.year}`}>{details.year}</span>}
    </div>
  )

  const internalSnapshotId = details.level === 'hyakumeiten' ? explicitSnapshotId : undefined

  const wrappedBadge = internalSnapshotId ? (
    <LocalizedLink
      to={`/rankings/$snapshotId`}
      params={{ snapshotId: internalSnapshotId }}
      className="inline-block p-0 m-0 bg-transparent border-none outline-none cursor-pointer appearance-none"
      onClick={(e: any) => e.stopPropagation()}
    >
      {BadgeContent}
    </LocalizedLink>
  ) : awardUrl ? (
    <a
      href={awardUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block p-0 m-0 bg-transparent border-none outline-none cursor-help appearance-none"
      onClick={(e) => e.stopPropagation()}
    >
      {BadgeContent}
    </a>
  ) : (
    <button
      type="button"
      className="inline-block p-0 m-0 bg-transparent border-none outline-none cursor-help appearance-none"
      onClick={(e) => e.stopPropagation()}
    >
      {BadgeContent}
    </button>
  )

  return (
    <Tooltip delay={0}>
      {wrappedBadge}
      <Tooltip.Content placement="top" showArrow={true}>
        <div className="font-['Noto_Sans_JP'] text-sm">{details.originalName}</div>
      </Tooltip.Content>
    </Tooltip>
  )
}
